import { useMemo } from 'react';

import { filterVisibleItems, isAnyItemActive } from 'components/shared/side-menu/side-menu.utils';

import type { NavItem } from 'components/shared/side-menu/nav-config';

import BaseCollapsedSectionWithIcon from './base-collapsed-section-with-icon';
import BaseCollapsedSection from './base-collapsed-section';
import BaseExpandedSection from './base-expanded-section';

interface BaseSideMenuSectionProps {
  title?: string;
  items: NavItem[];
  isCollapsed: boolean;
  isExpanded?: boolean;
  onToggle?: () => void;
  showToggle?: boolean;
  sectionIcon?: React.ReactNode;
  titleIcon?: React.ReactNode;
  isItemActive: (item: NavItem) => boolean;
  renderLink: (props: Record<string, unknown>) => React.ReactNode;
  getNotification?: (item: NavItem) => boolean;
  className?: string;
}

const BaseSideMenuSection = ({
  title,
  items,
  isCollapsed,
  isExpanded: sectionExpanded = true,
  onToggle,
  showToggle = false,
  sectionIcon,
  titleIcon,
  isItemActive,
  renderLink,
  getNotification,
  className,
}: BaseSideMenuSectionProps) => {
  const visibleItems = useMemo(() => filterVisibleItems(items), [items]);

  const isSectionActive = useMemo(
    () =>
      typeof isItemActive === 'function' &&
      isAnyItemActive(visibleItems, null, isItemActive),
    [visibleItems, isItemActive]
  );

  if (visibleItems.length === 0) {
    return null;
  }

  if (isCollapsed && sectionIcon) {
    return (
      <BaseCollapsedSectionWithIcon
        title={title}
        sectionIcon={sectionIcon}
        visibleItems={visibleItems}
        isSectionActive={isSectionActive}
        isItemActive={isItemActive}
        renderLink={renderLink}
        getNotification={getNotification}
        className={className}
      />
    );
  }

  if (isCollapsed) {
    return (
      <BaseCollapsedSection
        title={title}
        visibleItems={visibleItems}
        isCollapsed={isCollapsed}
        isItemActive={isItemActive}
        renderLink={renderLink}
        getNotification={getNotification}
        className={className}
      />
    );
  }

  return (
    <BaseExpandedSection
      title={title}
      titleIcon={titleIcon}
      visibleItems={visibleItems}
      isCollapsed={isCollapsed}
      sectionExpanded={sectionExpanded}
      showToggle={showToggle}
      onToggle={onToggle}
      isItemActive={isItemActive}
      renderLink={renderLink}
      getNotification={getNotification}
      className={className}
    />
  );
};

export default BaseSideMenuSection;
